import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { update } from "api/ioCredentialsApi";
import { topics as topicsApi } from "api/messagesApi";
import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { isMappableArray } from "util/helpers";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import withShowAlert from "components/withShowAlert";

const topicValue = (topic) => (Array.isArray(topic) ? topic[1] : topic);

const ManageTopics = ({
  open,
  setOpen,
  showAlert,
  ioCredential,
  onSaved,
}) => {
  const [enabledTopics, setEnabledTopics] = useState([]);
  const [disabledTopics, setDisabledTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedDisabledTopics, setSelectedDisabledTopics] = useState([]);
  const [selectedEnabledTopics, setSelectedEnabledTopics] = useState([]);

  const closeModal = () => {
    setOpen(false);
    setSelectedDisabledTopics([]);
    setSelectedEnabledTopics([]);
  };

  const arrayHasItems = (arr) => arr.length > 0;

  useEffect(() => {
    if (!open) return;

    const getTopics = async () => {
      setIsLoadingTopics(true);
      try {
        const response = await topicsApi();
        const allTopics = response.data || [];
        const currentlyEnabled = ioCredential?.enabled_import_topics || [];

        const enabled = allTopics
          .filter((topic) => currentlyEnabled.includes(topic))
          .map((topic) => [topic, topic]);
        const disabled = allTopics
          .filter((topic) => !currentlyEnabled.includes(topic))
          .map((topic) => [topic, topic]);

        setEnabledTopics(enabled);
        setDisabledTopics(disabled);
        setSelectedDisabledTopics([]);
        setSelectedEnabledTopics([]);
      } catch (error) {
        console.error(error);
        showAlert("Erro ao buscar tópicos.", "error");
      } finally {
        setIsLoadingTopics(false);
      }
    };

    getTopics();
  }, [open, ioCredential, showAlert]);

  const isTopicSelected = (topic, kind) => {
    switch (kind) {
      case "enabled":
        return selectedEnabledTopics.includes(topic);
      case "disabled":
        return selectedDisabledTopics.includes(topic);
      default:
        break;
    }
  };

  const setNewDisabledTopics = () => {
    setDisabledTopics((prevTopics) => [
      ...prevTopics,
      ...selectedEnabledTopics,
    ]);
    setEnabledTopics((prevTopics) =>
      prevTopics.filter((topic) => !selectedEnabledTopics.includes(topic))
    );
    setSelectedEnabledTopics([]);
  };

  const setNewEnabledTopics = () => {
    setEnabledTopics((prevTopics) => [
      ...prevTopics,
      ...selectedDisabledTopics,
    ]);
    setDisabledTopics((prevTopics) =>
      prevTopics.filter((topic) => !selectedDisabledTopics.includes(topic))
    );
    setSelectedDisabledTopics([]);
  };

  const toggleTopic = (topic, kind) => {
    switch (kind) {
      case "enabled":
        if (selectedEnabledTopics.includes(topic)) {
          setSelectedEnabledTopics(
            selectedEnabledTopics.filter((t) => t !== topic)
          );
        } else {
          setSelectedEnabledTopics([...selectedEnabledTopics, topic]);
        }
        break;
      case "disabled":
        if (selectedDisabledTopics.includes(topic)) {
          setSelectedDisabledTopics(
            selectedDisabledTopics.filter((t) => t !== topic)
          );
        } else {
          setSelectedDisabledTopics([...selectedDisabledTopics, topic]);
        }
        break;
      default:
        break;
    }
  };

  const updateCredentialTopics = async () => {
    if (!ioCredential?.id) {
      showAlert("Credencial não encontrada.", "error");
      return;
    }

    setIsSaving(true);
    try {
      const enabled_import_topics = enabledTopics.map(topicValue);
      await update(ioCredential.id, { enabled_import_topics });
      showAlert("Tópicos atualizados com sucesso.", "success");
      onSaved?.();
      closeModal();
    } catch (error) {
      const msg =
        error.response?.data?.message?.join?.("\n") ||
        "Erro ao salvar tópicos.";
      showAlert(msg, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={closeModal} fullWidth maxWidth="lg">
      <DialogTitle>
        <Typography variant="h4">Gerenciar Tópicos</Typography>
        <IconButton
          onClick={closeModal}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {isLoadingTopics ? (
          <Stack alignItems="center">
            <CircularProgress />
          </Stack>
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "baseline",
              gap: "1rem",
            }}
          >
            <Card sx={{ flex: 1, minHeight: 250 }}>
              <CardHeader
                title={
                  <Typography variant="h6">Tópicos desabilitados</Typography>
                }
              />
              <CardContent>
                <Grid container spacing={1}>
                  {isMappableArray(disabledTopics, (topic, index) => (
                    <Grid item key={index}>
                      <Chip
                        color="primary"
                        variant={
                          isTopicSelected(topic, "disabled")
                            ? "filled"
                            : "outlined"
                        }
                        label={topicValue(topic)}
                        onClick={() => toggleTopic(topic, "disabled")}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Button
                disabled={!arrayHasItems(selectedDisabledTopics) || isSaving}
                onClick={setNewEnabledTopics}
              >
                Habilitar
              </Button>
              <IconButton
                color="primary"
                disabled={
                  isSaving ||
                  (!arrayHasItems(selectedEnabledTopics) &&
                    !arrayHasItems(selectedDisabledTopics))
                }
                onClick={() => {
                  setNewDisabledTopics();
                  setNewEnabledTopics();
                }}
              >
                <SwapHorizIcon />
              </IconButton>
              <Button
                disabled={!arrayHasItems(selectedEnabledTopics) || isSaving}
                onClick={setNewDisabledTopics}
              >
                Desabilitar
              </Button>
            </Box>
            <Card sx={{ flex: 1, minHeight: 250 }}>
              <CardHeader
                title={
                  <Typography variant="h6">Tópicos habilitados</Typography>
                }
              />
              <CardContent>
                <Grid container spacing={1}>
                  {isMappableArray(enabledTopics, (topic, index) => (
                    <Grid item key={index}>
                      <Chip
                        color="primary"
                        variant={
                          isTopicSelected(topic, "enabled")
                            ? "filled"
                            : "outlined"
                        }
                        label={topicValue(topic)}
                        onClick={() => toggleTopic(topic, "enabled")}
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          color="primary"
          variant="outlined"
          onClick={closeModal}
          disabled={isSaving}
        >
          Cancelar
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={updateCredentialTopics}
          disabled={isSaving || isLoadingTopics || !ioCredential?.id}
        >
          {isSaving ? "Salvando..." : "Salvar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withShowAlert(ManageTopics);
